/**
 * Scroll Engine — central orchestrator for scroll-driven animations.
 *
 * Pipeline: Lenis (smooth layer) → ScrollTrigger (progress authority) → FSM → Interpolator → StyleComposer → DOM
 *
 * Lenis = smooth scroll normalization layer ONLY (via scrollerProxy).
 * ScrollTrigger = single source of truth for progress values.
 * FSM = sole logic owner for state transitions and DOM updates.
 *
 * The engine respects:
 *   - prefers-reduced-motion: reduce → fallback states, engine NOT started
 *   - Low-end devices (hardwareConcurrency <= 2 OR deviceMemory <= 2) → fallback states
 *   - Zero [data-scroll-section] elements → zero overhead (no Lenis, no ScrollTrigger, no RAF)
 *   - Shopify Design Mode events (shopify:section:load, shopify:section:reorder)
 *
 * RAF tick follows a 5-phase pipeline:
 *   1. collect  — Lenis RAF update
 *   2. evaluate — FSM via ScrollTrigger onUpdate
 *   3. prioritize — enforce max 5 ACTIVE sections
 *   4. compose  — StyleComposer resolve property ownership
 *   5. commit   — StyleComposer commit + clear
 *
 * @module scroll-engine
 */

import { StyleComposer } from './scroll-engine-style-composer.js';
import { VisibilityCuller } from './scroll-engine-visibility-culler.js';
import { SectionRegistry } from './scroll-engine-registry.js';
import { GlobalTimelineCoordinator } from './scroll-engine-coordinator.js';

import { DebugOverlay } from './scroll-engine-debug.js';

/**
 * Error logging categories for structured warnings.
 * @enum {string}
 */
const ErrorCategory = {
  CONFIG_PARSE: 'CONFIG_PARSE',
  LIFECYCLE_INVALID: 'LIFECYCLE_INVALID',
  DEPENDENCY_MISSING: 'DEPENDENCY_MISSING',
  BUDGET_EXCEEDED: 'BUDGET_EXCEEDED',
  CROSS_SECTION_CONFLICT: 'CROSS_SECTION_CONFLICT',
};

/** Maximum number of simultaneously ACTIVE sections */
const MAX_ACTIVE_SECTIONS = 5;

/** Resize debounce delay in ms */
const RESIZE_DEBOUNCE_MS = 150;


/**
 * Central scroll animation orchestrator.
 * Initializes Lenis as smooth scroll layer, ScrollTrigger as progress authority,
 * and manages the full lifecycle of scroll-driven sections.
 */
export class ScrollEngine {
  /** @type {Object|null} Lenis smooth scroll instance */
  _lenis = null;

  /** @type {GlobalTimelineCoordinator|null} */
  _coordinator = null;

  /** @type {SectionRegistry|null} */
  _registry = null;

  /** @type {StyleComposer|null} */
  _styleComposer = null;

  /** @type {VisibilityCuller|null} */
  _culler = null;

  /** @type {Object|null} DebugOverlay instance (Task 10) */
  _debugOverlay = null;

  /** @type {number|null} Current RAF handle */
  _rafId = null;

  /** @type {boolean} Whether the RAF loop is running */
  _running = false;

  /** @type {boolean} Whether the engine has been initialized */
  _initialized = false;

  /** @type {boolean} Whether the engine has been destroyed */
  _destroyed = false;

  /** @type {boolean} Whether debug mode is active */
  _debug = false;

  /** @type {number|null} Resize debounce timer */
  _resizeTimer = null;

  /** @type {Object} Engine configuration */
  _config = {};

  /** @type {Function|null} Bound tick for RAF */
  _boundTick = null;

  /** @type {Function|null} Bound visibility handler */
  _boundVisibilityHandler = null;

  /** @type {Function|null} Bound resize handler */
  _boundResizeHandler = null;

  /** @type {Function|null} Bound Shopify section load handler */
  _boundSectionLoadHandler = null;

  /** @type {Function|null} Bound Shopify section reorder handler */
  _boundSectionReorderHandler = null;

  /** @type {Function|null} GSAP ticker callback reference for cleanup */
  _gsapTickerCallback = null;

  /**
   * @param {Object} [config]
   * @param {number} [config.lerp=0.1] - Lenis smooth scroll lerp factor
   * @param {number} [config.duration=1.2] - Lenis smooth scroll duration
   * @param {boolean} [config.smoothWheel=true] - Enable smooth wheel scrolling
   * @param {boolean} [config.debug=false] - Enable debug overlay
   */
  constructor(config = {}) {
    this._config = {
      lerp: config.lerp ?? 0.1,
      duration: config.duration ?? 1.2,
      smoothWheel: config.smoothWheel !== false,
      debug: config.debug ?? false,
    };

    this._boundTick = (timestamp) => this.tick(timestamp);
    this._boundVisibilityHandler = () => this._handleVisibilityChange();
    this._boundResizeHandler = () => this._handleResize();
    this._boundSectionLoadHandler = () => this.refresh();
    this._boundSectionReorderHandler = () => this.refresh();
  }

  /**
   * Initialize the scroll engine.
   *
   * Order:
   *   1. Check reduced-motion → apply fallback states and bail
   *   2. Check low-end device → apply fallback states and bail
   *   3. Check [data-scroll-section] count → if 0, do nothing (zero overhead)
   *   4. Initialize Lenis as smooth scroll layer via ScrollTrigger.scrollerProxy()
   *   5. Create GlobalTimelineCoordinator
   *   6. Scan sections
   *   7. Start RAF loop
   */
  init() {
    if (this._initialized || this._destroyed) return;

    // 1. Check reduced-motion preference (FR-1.6)
    if (this._prefersReducedMotion()) {
      this.applyFallbackStates();
      return;
    }

    // 2. Check low-end device (FR-20.4, FR-20.5) — OR logic
    if (this._isLowEndDevice()) {
      this.applyFallbackStates();
      return;
    }

    // 3. Check for scroll sections — zero overhead if none (FR-18.1)
    const sections = document.querySelectorAll('[data-scroll-section]');
    if (sections.length === 0) return;

    // Resolve debug mode (FR-1.8)
    this._debug = this._resolveDebugMode();

    // 4. Initialize core systems
    this._styleComposer = new StyleComposer();
    this._coordinator = new GlobalTimelineCoordinator();
    this._registry = new SectionRegistry();

    // Initialize Lenis smooth scroll layer (FR-1.1)
    this._initLenis();

    // 5. Initialize VisibilityCuller for lifecycle management
    this._culler = new VisibilityCuller((el, isVisible, isNearViewport) => {
      this._handleVisibilityCallback(el, isVisible, isNearViewport);
    });

    // 6. Scan and register sections (FR-1.4)
    this._scanSections(sections);

    // Parse cross-section dependencies
    this._coordinator.parseDependencies(this._registry.getAll());

    // 7. Initialize debug overlay if enabled (Task 10 — conditional)
    this._initDebugOverlay();

    // 8. Bind event listeners
    this._bindEvents();

    // 9. Start RAF loop (FR-1.5)
    this._initialized = true;
    this._running = true;
    this._rafId = requestAnimationFrame(this._boundTick);
  }


  /**
   * Main RAF tick — 5-phase pipeline (FR-26.1–FR-26.5).
   *
   * Phase 1: collect  — Lenis RAF update (smooth scroll normalization)
   * Phase 2: evaluate — FSM evaluation via ScrollTrigger onUpdate (handled by ScrollTrigger callbacks)
   * Phase 3: prioritize — enforce max 5 ACTIVE sections (FR-25.1–FR-25.5)
   * Phase 4: compose  — StyleComposer resolve property ownership
   * Phase 5: commit   — StyleComposer commit + clear
   *
   * @param {number} timestamp - DOMHighResTimeStamp from requestAnimationFrame
   */
  tick(timestamp) {
    if (!this._running || this._destroyed) return;

    const debugTiming = this._debug ? performance.now() : 0;

    // Phase 1: collect — Lenis RAF update
    if (this._lenis) {
      this._lenis.raf(timestamp);
    }

    // Phase 2: evaluate — ScrollTrigger onUpdate callbacks fire during Lenis.raf()
    // which calls ScrollTrigger.update(). Each SectionController.update() is called
    // by its own ScrollTrigger onUpdate, feeding progress to FSM.
    // Cross-section events are evaluated after all sections report progress.
    this._coordinator.evaluateCrossSectionEvents();

    // Phase 3: prioritize — enforce max 5 ACTIVE sections
    this.enforceSectionPriority();

    // Phase 4: compose — resolve property ownership
    this._styleComposer.resolvePropertyOwnership();

    // Phase 5: commit — batched DOM writes + clear for next frame
    this._styleComposer.commitStyles();
    this._styleComposer.clear();

    // Debug: measure frame time and check budget (NFR-1.1)
    if (this._debug && debugTiming) {
      const frameTime = performance.now() - debugTiming;
      if (frameTime > 4) {
        this._logWarning(ErrorCategory.BUDGET_EXCEEDED, null, {
          frameTime: `${frameTime.toFixed(2)}ms`,
          budget: '4ms',
        });
      }
    }

    // Update debug overlay (throttled internally to 15 FPS)
    if (this._debugOverlay) {
      this._debugOverlay.update(this._registry.getAll());
    }

    // Schedule next frame
    this._rafId = requestAnimationFrame(this._boundTick);
  }

  /**
   * Enforce section priority — max 5 ACTIVE sections (NFR-1.2, FR-25.1–FR-25.5).
   * Calculates distance to viewport center, keeps top 5 closest ACTIVE, pauses rest.
   * Priority is deterministic: same scroll position = same priority order.
   */
  enforceSectionPriority() {
    const active = this._registry.getActive();
    if (active.length <= MAX_ACTIVE_SECTIONS) return;

    const viewportCenter = window.innerHeight / 2;

    // Calculate distance to viewport center for each active section
    const scored = active.map((controller) => {
      const rect = controller.el?.getBoundingClientRect();
      if (!rect) return { controller, distance: Infinity };
      const sectionCenter = rect.top + rect.height / 2;
      return { controller, distance: Math.abs(sectionCenter - viewportCenter) };
    });

    // Sort by distance (closest first) — deterministic ordering
    scored.sort((a, b) => a.distance - b.distance);

    // Pause sections beyond the top 5
    for (let i = MAX_ACTIVE_SECTIONS; i < scored.length; i++) {
      const { controller } = scored[i];
      if (controller.lifecycle === 'ACTIVE') {
        controller.transitionLifecycle('INACTIVE');
      }
    }
  }


  /**
   * Handle tab visibility change (FR-10.1–FR-10.5).
   * Pause RAF on hidden, resume + ScrollTrigger.refresh() + Lenis reset on visible.
   * @private
   */
  _handleVisibilityChange() {
    if (document.hidden) {
      // Pause RAF loop (FR-10.1)
      this._running = false;
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
    } else {
      // Resume (FR-10.2)
      this._running = true;

      // Refresh ScrollTrigger to recalculate trigger positions (FR-10.3)
      const ST = globalThis.ScrollTrigger;
      if (ST) {
        ST.refresh();
      }

      // Reset Lenis internal time delta to prevent jump on resume (FR-10.5)
      if (this._lenis) {
        this._lenis.start();
      }

      // Re-evaluate FSM on current scroll position — no stale state (FR-10.4)
      const controllers = this._registry.getAll();
      for (const controller of controllers) {
        if (controller.lifecycle === 'ACTIVE' && controller.fsm && controller.scrollTrigger) {
          controller.update(controller.scrollTrigger.progress ?? controller._lastProgress);
        }
      }

      // Restart RAF loop
      this._rafId = requestAnimationFrame(this._boundTick);
    }
  }

  /**
   * Handle window resize — debounced at 150ms (NFR-6.1, FR-11.1–FR-11.4).
   * ScrollTrigger.refresh() recalculates all trigger positions.
   * FSM re-evaluates on current progress — state may change.
   * Lifecycle states are preserved — no re-mount needed.
   * @private
   */
  _handleResize() {
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
    }

    this._resizeTimer = setTimeout(() => {
      this._resizeTimer = null;

      // Refresh ScrollTrigger (FR-11.1, FR-11.2)
      const ST = globalThis.ScrollTrigger;
      if (ST) {
        ST.refresh();
      }

      // Re-evaluate FSM on current progress (FR-11.3)
      const controllers = this._registry.getAll();
      for (const controller of controllers) {
        if (controller.lifecycle === 'ACTIVE' && controller.fsm && controller.scrollTrigger) {
          controller.update(controller.scrollTrigger.progress ?? controller._lastProgress);
        }
      }
    }, RESIZE_DEBOUNCE_MS);
  }

  /**
   * Refresh engine for Shopify Design Mode events (FR-1.7).
   * Re-scans DOM, reconciles registry, re-parses dependencies.
   * Called on shopify:section:load and shopify:section:reorder.
   */
  refresh() {
    if (!this._initialized || this._destroyed) return;

    // Re-scan DOM and reconcile registry
    this._registry.refresh();

    // Re-observe new sections with visibility culler
    const controllers = this._registry.getAll();
    for (const controller of controllers) {
      if (controller.el && controller.lifecycle === 'UNMOUNTED') {
        // Wire up new controllers
        controller.styleComposer = this._styleComposer;
        controller.coordinator = this._coordinator;
        this._culler.observe(controller.el);
      }
    }

    // Re-parse cross-section dependencies
    this._coordinator.destroy();
    this._coordinator = new GlobalTimelineCoordinator();
    this._coordinator.parseDependencies(controllers);

    // Update coordinator reference on all controllers
    for (const controller of controllers) {
      controller.coordinator = this._coordinator;
    }

    // Refresh ScrollTrigger positions
    const ST = globalThis.ScrollTrigger;
    if (ST) {
      ST.refresh();
    }
  }


  /**
   * Apply fallback states for reduced-motion or low-end devices (FR-1.6, FR-20.4).
   * Removes sticky positioning, applies expanded state styles,
   * sets opacity 1 and transform none on all targets.
   */
  applyFallbackStates() {
    const sections = document.querySelectorAll('[data-scroll-section]');
    for (const section of sections) {
      // Remove sticky positioning
      section.style.position = 'relative';
      const sticky = section.querySelector('[data-scroll-sticky]');
      if (sticky) {
        sticky.style.position = 'relative';
        sticky.style.top = 'auto';
        sticky.style.height = 'auto';
      }

      // Apply expanded state — opacity 1, transform none on all animated targets
      section.style.opacity = '1';
      section.style.transform = 'none';

      const targets = section.querySelectorAll('[data-scroll-animate]');
      for (const target of targets) {
        target.style.opacity = '1';
        target.style.transform = 'none';
        target.style.clipPath = 'none';
        target.style.filter = 'none';
      }
    }
  }

  /**
   * Destroy the engine — full cleanup (FR-1.9, FR-24.5–FR-24.7).
   * Cancel RAF, remove GSAP ticker callbacks, remove Lenis scroll listeners,
   * clean up ScrollTrigger.scrollerProxy, destroy all controllers,
   * destroy coordinator, destroy StyleComposer, destroy Lenis, destroy debug overlay.
   */
  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    this._running = false;

    // Cancel RAF (FR-1.9)
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }

    // Remove GSAP ticker callbacks (FR-24.5)
    const gsap = globalThis.gsap;
    if (gsap && this._gsapTickerCallback) {
      gsap.ticker.remove(this._gsapTickerCallback);
      this._gsapTickerCallback = null;
    }

    // Unbind event listeners
    this._unbindEvents();

    // Clear resize timer
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }

    // Destroy all controllers via registry
    if (this._registry) {
      const controllers = this._registry.getAll();
      for (const controller of controllers) {
        if (controller.config) {
          this._registry.deregister(controller.config.id);
        }
      }
      this._registry = null;
    }

    // Destroy coordinator
    if (this._coordinator) {
      this._coordinator.destroy();
      this._coordinator = null;
    }

    // Destroy StyleComposer
    if (this._styleComposer) {
      this._styleComposer.destroy();
      this._styleComposer = null;
    }

    // Destroy VisibilityCuller
    if (this._culler) {
      this._culler.destroy();
      this._culler = null;
    }

    // Clean up ScrollTrigger.scrollerProxy (FR-24.7)
    const ST = globalThis.ScrollTrigger;
    if (ST && this._lenis) {
      try {
        ST.clearScrollMemory();
      } catch (_) {
        // ScrollTrigger may not support clearScrollMemory
      }
    }

    // Remove Lenis scroll listeners and destroy (FR-24.6)
    if (this._lenis) {
      this._lenis.destroy();
      this._lenis = null;
    }

    // Destroy debug overlay
    if (this._debugOverlay) {
      this._debugOverlay.destroy();
      this._debugOverlay = null;
    }

    this._initialized = false;
  }


  // ─── Private Helpers ───────────────────────────────────────────────

  /**
   * Check if user prefers reduced motion (FR-1.6).
   * @returns {boolean}
   * @private
   */
  _prefersReducedMotion() {
    return typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
  }

  /**
   * Check if device is low-end (FR-20.4, FR-20.5).
   * Uses OR logic — one match on CPU or RAM is sufficient for degradation.
   * @returns {boolean}
   * @private
   */
  _isLowEndDevice() {
    const nav = typeof navigator !== 'undefined' ? navigator : {};
    const lowCPU = (nav.hardwareConcurrency ?? Infinity) <= 2;
    const lowRAM = (nav.deviceMemory ?? Infinity) <= 2;
    return lowCPU || lowRAM;
  }

  /**
   * Resolve debug mode from multiple sources (FR-1.8).
   * Priority: URL param > window.__surprav.scrollDebug > config.debug
   * @returns {boolean}
   * @private
   */
  _resolveDebugMode() {
    // URL parameter check
    if (typeof window !== 'undefined' && window.location) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('scroll-debug')) return true;
    }

    // Global config check
    if (typeof window !== 'undefined' && window.__surprav?.scrollDebug) return true;

    return this._config.debug;
  }

  /**
   * Initialize Lenis smooth scroll layer and register as ScrollTrigger.scrollerProxy (FR-1.1).
   * Lenis provides smooth scroll normalization ONLY — no progress values.
   * ScrollTrigger remains the single source of truth for progress.
   * @private
   */
  _initLenis() {
    const LenisClass = globalThis.Lenis;
    const gsap = globalThis.gsap;
    const ST = globalThis.ScrollTrigger;

    if (!LenisClass) {
      this._logWarning(ErrorCategory.DEPENDENCY_MISSING, null, {
        dependency: 'Lenis',
        fallback: 'Native scroll (no smooth normalization)',
      });
      return;
    }

    if (!ST) {
      this._logWarning(ErrorCategory.DEPENDENCY_MISSING, null, {
        dependency: 'ScrollTrigger',
        fallback: 'No scroll-driven animations',
      });
      return;
    }

    // Create Lenis instance (FR-9.1)
    this._lenis = new LenisClass({
      lerp: this._config.lerp,
      duration: this._config.duration,
      smoothWheel: this._config.smoothWheel,
      // Touch events use passive listeners (NFR-5.1, FR-9.4)
      touchMultiplier: 2,
    });

    // Register Lenis as ScrollTrigger.scrollerProxy (FR-1.1, FR-9.2)
    // This makes ScrollTrigger use Lenis's smoothed scroll position
    ST.scrollerProxy(document.documentElement, {
      scrollTop(value) {
        if (arguments.length) {
          // Setter — Lenis scroll to position
          this._lenis?.scrollTo(value, { immediate: true });
        }
        // Getter — return Lenis's current scroll position
        return this._lenis?.scroll ?? window.scrollY;
      },
      getBoundingClientRect() {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        };
      },
    });

    // Connect Lenis scroll events to ScrollTrigger.update()
    this._lenis.on('scroll', () => {
      ST.update();
    });

    // Register GSAP ticker callback for Lenis RAF sync
    if (gsap) {
      this._gsapTickerCallback = (time) => {
        this._lenis?.raf(time * 1000);
      };
      gsap.ticker.add(this._gsapTickerCallback);
      // Disable Lenis's own RAF since GSAP ticker drives it
      gsap.ticker.lagSmoothing(0);
    }
  }

  /**
   * Scan DOM for [data-scroll-section] elements and register them (FR-1.4).
   * @param {NodeList} sections - Queried section elements
   * @private
   */
  _scanSections(sections) {
    for (const el of sections) {
      const controller = this._registry.register(el);

      // Wire up shared references
      controller.styleComposer = this._styleComposer;
      controller.coordinator = this._coordinator;

      // Start observing for visibility-based lifecycle transitions
      this._culler.observe(el);
    }
  }

  /**
   * Handle VisibilityCuller callback — drive lifecycle transitions (FR-6.2, FR-6.3).
   * @param {HTMLElement} el
   * @param {boolean} isVisible - In viewport (intersectionRatio > 0)
   * @param {boolean} isNearViewport - Within 200vh rootMargin
   * @private
   */
  _handleVisibilityCallback(el, isVisible, isNearViewport) {
    // Find controller for this element
    const controllers = this._registry.getAll();
    const controller = controllers.find((c) => c.el === el);
    if (!controller) return;

    const lifecycle = controller.lifecycle;

    if (isNearViewport && lifecycle === 'UNMOUNTED') {
      // Near viewport → MOUNTED (lazy mount)
      controller.transitionLifecycle('MOUNTED');
    }

    if (isVisible && (lifecycle === 'MOUNTED' || lifecycle === 'INACTIVE')) {
      // In viewport → ACTIVE
      controller.transitionLifecycle('ACTIVE');

      // Rehydration: evaluate FSM on current scroll position immediately (FR-13.1, FR-13.2)
      if (controller.scrollTrigger) {
        controller.update(controller.scrollTrigger.progress ?? 0);
      }
    }

    if (!isVisible && !isNearViewport && lifecycle === 'ACTIVE') {
      // Out of viewport → INACTIVE
      controller.transitionLifecycle('INACTIVE');
    }
  }


  /**
   * Initialize debug overlay if debug mode is active (FR-1.8).
   * @private
   */
  _initDebugOverlay() {
    if (!this._debug) return;

    this._debugOverlay = new DebugOverlay(this._coordinator);
    this._debugOverlay.mount();
  }

  /**
   * Bind all event listeners.
   * All scroll/touch event listeners use { passive: true } (NFR-5.1).
   * @private
   */
  _bindEvents() {
    // Tab visibility handler (FR-10.1–FR-10.5)
    document.addEventListener('visibilitychange', this._boundVisibilityHandler);

    // Debounced resize handler (NFR-6.1, FR-11.1–FR-11.4)
    window.addEventListener('resize', this._boundResizeHandler, { passive: true });

    // Shopify Design Mode events (FR-1.7)
    document.addEventListener('shopify:section:load', this._boundSectionLoadHandler);
    document.addEventListener('shopify:section:reorder', this._boundSectionReorderHandler);
  }

  /**
   * Unbind all event listeners.
   * @private
   */
  _unbindEvents() {
    document.removeEventListener('visibilitychange', this._boundVisibilityHandler);
    window.removeEventListener('resize', this._boundResizeHandler);
    document.removeEventListener('shopify:section:load', this._boundSectionLoadHandler);
    document.removeEventListener('shopify:section:reorder', this._boundSectionReorderHandler);
  }

  /**
   * Structured error logging contract (ERR-1.1–ERR-1.3, ERR-2.1–ERR-2.4).
   * Categories: CONFIG_PARSE, LIFECYCLE_INVALID, DEPENDENCY_MISSING, BUDGET_EXCEEDED, CROSS_SECTION_CONFLICT
   *
   * @param {string} category - Error category from ErrorCategory enum
   * @param {string|null} sectionId - Related section ID (null for global errors)
   * @param {Object} details - Additional context
   * @private
   */
  _logWarning(category, sectionId, details) {
    console.warn('[ScrollEngine]', {
      category,
      sectionId,
      timestamp: Date.now(),
      details,
    });
  }
}

// ─── Auto-initialization ─────────────────────────────────────────────

/**
 * Initialize ScrollEngine on DOMContentLoaded (FR-20.6).
 * Reads config from window.__surprav.scrollEngine (SPC-5.1).
 */
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    const config = (typeof window !== 'undefined' && window.__surprav?.scrollEngine) || {};
    const engine = new ScrollEngine(config);
    engine.init();

    // Expose engine instance for Shopify Design Mode and external access
    if (typeof window !== 'undefined') {
      window.__surprav = window.__surprav || {};
      window.__surprav._scrollEngineInstance = engine;
    }
  });
}
